import { Color, CAPACITY, GameState } from './types';
import pack from './levels.json';

// ── Color ids ─────────────────────────────────────────────────────────────
// Logical color identifiers; index maps to CSS var --col-N / theme.colors[N].
export const COLOR_IDS: Color[] = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6'];

// ── Core game logic ───────────────────────────────────────────────────────

function topColor(tube: Color[]): Color | null {
  return tube.length > 0 ? tube[tube.length - 1] : null;
}

export function canPour(tubes: Color[][], from: number, to: number): boolean {
  if (from === to) return false;
  const src = tubes[from];
  const dst = tubes[to];
  if (src.length === 0) return false;
  if (dst.length >= CAPACITY) return false;
  const srcTop = topColor(src)!;
  if (dst.length === 0) return true;
  return topColor(dst) === srcTop;
}

export function applyPour(
  tubes: Color[][],
  from: number,
  to: number,
): { newTubes: Color[][]; movedColors: Color[] } {
  const src = [...tubes[from]];
  const dst = [...tubes[to]];

  const srcTop = topColor(src)!;
  let count = 0;
  for (let i = src.length - 1; i >= 0; i--) {
    if (src[i] === srcTop) count++;
    else break;
  }
  count = Math.min(count, CAPACITY - dst.length);

  const moved = src.splice(src.length - count, count);
  dst.push(...moved);

  const newTubes = tubes.map((t, i) => {
    if (i === from) return src;
    if (i === to)   return dst;
    return [...t];
  });

  return { newTubes, movedColors: moved };
}

export function isTubeDone(tube: Color[]): boolean {
  return tube.length === CAPACITY && tube.every(c => c === tube[0]);
}

export function isWon(tubes: Color[][]): boolean {
  return tubes.every(t => t.length === 0 || isTubeDone(t));
}

// ── Pack loader ───────────────────────────────────────────────────────────
export const PACK_SIZE = 60;
export const PLATEAU_START = 39; // 0-based; level 40

export function packIndex(levelIdx: number): number {
  if (levelIdx < PACK_SIZE) return levelIdx;
  const loopLen = PACK_SIZE - PLATEAU_START;
  return PLATEAU_START + ((levelIdx - PACK_SIZE) % loopLen);
}

export function loadLevel(levelIdx: number): Color[][] {
  return (pack as Color[][][])[packIndex(levelIdx)].map(tube => [...tube]);
}

// ── State factory ─────────────────────────────────────────────────────────
export function createState(levelIdx: number): GameState {
  return {
    tubes: loadLevel(levelIdx),
    selected: null,
    phase: 'playing',
    anim: null,
    history: [],
    level: levelIdx,
    shake: null,
  };
}
